import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export async function exportarSATExcel(vuelos, servicios_fijos, nombreArchivo = "reporte_mensial.xlsx") {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Reporte Mensual");

  // --- 1. Estilos Comunes ---
  const bold = { bold: true };
  const centerAlignment = { vertical: "middle", horizontal: "center", wrapText: true };
  const rightAlignment = { vertical: "middle", horizontal: "right", wrapText: true };
  const blueFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF007BFF" } }; // Azul similar al PDF
  const whiteFont = { color: { argb: "FFFFFFFF" } };

  // --- 2. Preparación de Cabeceras Dinámicas (Servicios) ---
  const serviciosByNameMap = {};
  const serviciosByIdMap = {};

  Object.entries(servicios_fijos).forEach(([id, serv]) => {
    serviciosByNameMap[serv.nombre] = serv;
    serviciosByIdMap[id] = serv;
  });

  // --- 3. Cabeceras Fila 1 (Títulos Principales y Fecha) ---
  const headerRow1 = worksheet.addRow([]);
  headerRow1.getCell(1).value = "DAS POR FECHAS EJM";
  headerRow1.getCell(8).value = `FECHA DE REPORTE: ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  headerRow1.font = bold;
  headerRow1.getCell(1).alignment = centerAlignment;
  headerRow1.getCell(8).alignment = rightAlignment;

  worksheet.mergeCells(headerRow1.getCell(1).address, headerRow1.getCell(4).address);
  worksheet.mergeCells(headerRow1.getCell(8).address, headerRow1.getCell(12).address); // Total de 12 columnas finales

  // --- 4. Cabeceras Fila 2 (Cabeceras Fijas y "SERVICIO DE ASISTENCIA...") ---
  const row2Data = [
    "N",
    "NUMERO DE VUELO",
    "FECHA",
    "DIA DE VUELO",
    "N CONSTANCIA",
    "SERVICIO",
    "CONTRATO ACUERDO A IMPORTE DE CONSTANCIA",
    "CANT",
    "TOTAL ($)",
    "IMPORTE EN ($)",
    "IMPORTE EN (Bs)",
    "OBS",
  ];
  const headerRow2 = worksheet.addRow(row2Data);

  worksheet.getRow(2).eachCell(cell => {
    cell.font = bold;
    cell.alignment = centerAlignment;
    cell.fill = blueFill;
    cell.font = whiteFont;
  });

  // --- 5. Llenado de Filas de Vuelos y Servicios (Verticalmente) ---
  vuelos.forEach((vuelo, vueloIndex) => {
    const diaDeVuelo = new Date(vuelo.fecha).toLocaleDateString('es-ES', { weekday: 'long' }).toUpperCase();

    let serviciosDelVuelo = [];
    if (Array.isArray(vuelo.servicios)) {
      serviciosDelVuelo = vuelo.servicios;
    } else if (typeof vuelo.servicios === 'object' && vuelo.servicios !== null) {
      serviciosDelVuelo = Object.entries(vuelo.servicios).map(([serviceId, serviceDetails]) => {
        const fixedService = serviciosByIdMap[serviceId];
        return {
          servicio: fixedService ? fixedService.nombre : `ID ${serviceId} Desconocido`,
          valor_unitario: fixedService ? fixedService.valor_unitario : 0,
          constancias: serviceDetails.constancias,
          observaciones: serviceDetails.observaciones,
        };
      });
    }

    let vueloCurrentTotalDolares = 0;
    let vueloCurrentTotalBolivianos = 0;
    const vueloTipoCambio = parseFloat(vuelo.tipo_cambio || 6.96);

    if (serviciosDelVuelo.length === 0) {
      const row = [
        vueloIndex + 1,
        vuelo.numero_vuelo,
        vuelo.fecha,
        diaDeVuelo,
        vuelo.numero_constancia,
        "No hay servicios", "", "", "", "", "", "",
      ];
      worksheet.addRow(row);

      const totalVueloRow = worksheet.addRow([]);
      worksheet.mergeCells(totalVueloRow.getCell(1).address, totalVueloRow.getCell(8).address);
      totalVueloRow.getCell(1).value = `TOTAL VUELO ${vuelo.numero_vuelo}:`;
      totalVueloRow.getCell(9).value = vueloCurrentTotalDolares.toFixed(2);
      worksheet.mergeCells(totalVueloRow.getCell(9).address, totalVueloRow.getCell(10).address);
      totalVueloRow.getCell(11).value = vueloCurrentTotalBolivianos.toFixed(2);
      totalVueloRow.getCell(12).value = vuelo.observaciones || "";
      totalVueloRow.font = bold;
      totalVueloRow.getCell(9).alignment = rightAlignment;
      totalVueloRow.getCell(11).alignment = rightAlignment;
      totalVueloRow.getCell(12).alignment = centerAlignment;

      vuelo._calculatedTotalDolares = vueloCurrentTotalDolares;
      vuelo._calculatedTotalBolivianos = vueloCurrentTotalBolivianos;
      worksheet.addRow([]);
      return;
    }

    serviciosDelVuelo.forEach((serv, serviceIndex) => {
      const row = [];

      if (serviceIndex === 0) {
        row.push(
          vueloIndex + 1,
          vuelo.numero_vuelo,
          vuelo.fecha,
          diaDeVuelo,
          vuelo.numero_constancia
        );
      } else {
        row.push("", "", "", "", "");
      }

      const valorUnitario = serv.valor_unitario !== undefined ? serv.valor_unitario : (serviciosByNameMap[serv.servicio]?.valor_unitario ?? 0);
      const constanciasNum = parseFloat(serv.constancias || 0);
      const valorUnitarioNum = parseFloat(valorUnitario || 0);

      const totalServicioDolares = (constanciasNum * valorUnitarioNum);
      const totalServicioBs = (totalServicioDolares * vueloTipoCambio);

      vueloCurrentTotalDolares += totalServicioDolares;
      vueloCurrentTotalBolivianos += totalServicioBs;

      row.push(
        serv.servicio,
        valorUnitarioNum.toFixed(2),
        constanciasNum,
        totalServicioDolares.toFixed(2),
        totalServicioDolares.toFixed(2),
        totalServicioBs.toFixed(2),
        serv.observaciones || ""
      );

      worksheet.addRow(row);
    });

    const totalVueloRow = worksheet.addRow([]);
    worksheet.mergeCells(totalVueloRow.getCell(1).address, totalVueloRow.getCell(8).address);
    totalVueloRow.getCell(1).value = `TOTAL VUELO ${vuelo.numero_vuelo}:`;
    totalVueloRow.getCell(9).value = vueloCurrentTotalDolares.toFixed(2);
    worksheet.mergeCells(totalVueloRow.getCell(9).address, totalVueloRow.getCell(10).address);
    totalVueloRow.getCell(11).value = vueloCurrentTotalBolivianos.toFixed(2);
    totalVueloRow.getCell(12).value = vuelo.observaciones || "";

    totalVueloRow.font = bold;
    totalVueloRow.getCell(9).alignment = rightAlignment;
    totalVueloRow.getCell(11).alignment = rightAlignment;
    totalVueloRow.getCell(12).alignment = centerAlignment;

    vuelo._calculatedTotalDolares = vueloCurrentTotalDolares;
    vuelo._calculatedTotalBolivianos = vueloCurrentTotalBolivianos;

    worksheet.addRow([]);
  });

  // --- 6. Totales al final (TOTAL GENERAL) ---
  let grandTotalDolares = 0;
  let grandTotalBolivianos = 0;
  vuelos.forEach(vuelo => {
    grandTotalDolares += parseFloat(vuelo._calculatedTotalDolares || 0);
    grandTotalBolivianos += parseFloat(vuelo._calculatedTotalBolivianos || 0);
  });

  const footerRow = worksheet.addRow([]);

  // Etiqueta "TOTAL GENERAL ($):" en columna 8
  footerRow.getCell(8).value = "TOTAL GENERAL ($):";
  footerRow.getCell(8).font = bold;
  footerRow.getCell(8).alignment = rightAlignment;

  // Valor "TOTAL GENERAL ($)" en columna 9 (fusionada con 10)
  footerRow.getCell(9).value = grandTotalDolares.toFixed(2);
  worksheet.mergeCells(footerRow.getCell(9).address, footerRow.getCell(10).address);
  footerRow.getCell(9).font = bold;
  footerRow.getCell(9).alignment = rightAlignment;

  // Etiqueta "TOTAL GENERAL (Bs):" en columna 11
  footerRow.getCell(11).value = "TOTAL GENERAL (Bs):";
  footerRow.getCell(11).font = bold;
  footerRow.getCell(11).alignment = rightAlignment;

  // Valor "TOTAL GENERAL (Bs)" en columna 12
  footerRow.getCell(12).value = grandTotalBolivianos.toFixed(2);
  footerRow.getCell(12).font = bold;
  footerRow.getCell(12).alignment = rightAlignment;


  // --- 7. Ajustes finales: ancho de columnas y bordes ---
  worksheet.columns.forEach((col, index) => {
    switch (index) {
      case 0: col.width = 5; break; // N
      case 1: col.width = 15; break; // NUMERO DE VUELO
      case 2: col.width = 12; break; // FECHA
      case 3: col.width = 12; break; // DIA DE VUELO
      case 4: col.width = 15; break; // N CONSTANCIA
      case 5: col.width = 30; break; // SERVICIO
      case 6: col.width = 20; break; // CONTRATO
      case 7: col.width = 10; break; // CANT
      case 8: col.width = 15; break; // TOTAL ($)
      case 9: col.width = 15; break; // IMPORTE EN ($)
      case 10: col.width = 15; break; // IMPORTE EN (Bs)
      case 11: col.width = 25; break; // OBS (Columna 12)
      default: col.width = 15; break;
    }
  });

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const isHeaderMergedCell1 = rowNumber === 1 && colNumber >= 1 && colNumber <= 4;
      const isHeaderMergedCell2 = rowNumber === 1 && colNumber >= 8 && colNumber <= 12;

      if (isHeaderMergedCell1 || isHeaderMergedCell2) {
        return;
      }

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (rowNumber > 2) {
         cell.alignment = centerAlignment;
      }
    });
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber > 2) {
      row.getCell(9).alignment = rightAlignment;
      row.getCell(10).alignment = rightAlignment;
      row.getCell(11).alignment = rightAlignment;
      row.getCell(12).alignment = centerAlignment;

      const labelCell = row.getCell(1);
      if (labelCell.value && typeof labelCell.value === 'string' && labelCell.value.includes('TOTAL VUELO')) {
          row.getCell(9).alignment = rightAlignment;
          row.getCell(11).alignment = rightAlignment;
          row.getCell(12).alignment = centerAlignment;
      }
    }
  });

  // --- 8. Descargar archivo ---
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), nombreArchivo);
}